/**
 * Die Darstellung einer erwogenen Aktion — §36.
 *
 * **Die Preview ist eine zweite Lage, keine Verschiebung.** Sie verändert den `BoardState` nicht
 * (§33); erst der Commit tut das (§34). Auf dem Brett stehen deshalb die echten Figuren an ihren
 * echten Feldern, und darüber liegen durchscheinende Schatten auf den Feldern, auf denen sie
 * **wären**, wenn der Zug bestätigt würde.
 *
 * **Hier wird gelesen, nicht gerechnet.** `PushAction` trägt `pusherTo`, `victimTo` und
 * `capturedByPush` schon mit — die Auszahlung aus Phase 3d. §36 verlangt beim Push beide Figuren
 * auf ihren Endpositionen; nachzurechnen wäre das eine zweite Wahrheit über eine Regel, die die
 * Engine schon entschieden hat.
 *
 * **Drei bewusste Feinheiten:**
 *
 * - **Der Stoßer bekommt nur einen Schatten, wenn er sich bewegt.** Mit dem Default
 *   `pusherAdvance: 0` bleibt er stehen (`RULE_CONFIG.md`), und ein Schatten auf seinem eigenen
 *   Feld wäre eine Figur, die zweimal dasteht — keine Aussage über den Zug.
 * - **Eine Figur, die vom Brett geht, bekommt keinen Schatten, sondern eine Markierung.** Das
 *   betrifft das Opfer eines Schlags (§21) und einen Push über den Rand (`capturedByPush`): es
 *   gibt kein Zielfeld, auf dem sie stünde.
 * - **Fail closed.** Zeigt eine Preview auf eine Figur, die dieser Client nicht kennt, bleibt das
 *   Brett leer. Vorkommen sollte das nicht — der Server löst nur auf, was er selbst gerechnet hat
 *   —, aber raten ist hier die falsche Antwort.
 */

import {
  capturedOf,
  pieceById,
  playerOf,
  hexEquals,
  type GameState,
  type SectorId,
} from '@flaghex/engine';

import type { Hex } from '@flaghex/engine';

import type { BoardPiece } from '../board/BoardSvg.js';

/** Die aufgelöste Preview, wie sie über den WebSocket kommt (`PREVIEW`, `ws-messages.ts`). */
interface PreviewAction {
  readonly type: string;
  readonly player: string;
  readonly piece: string;
  readonly to: { readonly q: number; readonly r: number };
  readonly from?: { readonly q: number; readonly r: number } | undefined;
  readonly victim?: string | undefined;
  readonly victimAt?: { readonly q: number; readonly r: number } | undefined;
  readonly victimTo?: { readonly q: number; readonly r: number } | undefined;
  readonly capturedByPush?: boolean | undefined;
  readonly pusherTo?: { readonly q: number; readonly r: number } | undefined;
}

export interface PreviewFigures {
  /** Die Figuren auf ihren Endpositionen — durchscheinend zu zeichnen. */
  readonly ghosts: readonly BoardPiece[];
  /** Figuren, die diese Aktion vom Brett nimmt (§21, Randwurf nach §18). */
  readonly doomed: readonly string[];
  /**
   * Von wo nach wo — die Spur einer erwogenen Bewegung (Designphase, beschlossen 2026-10-05).
   *
   * §63 verlangt, dass **unterscheidbar bleibt, wo die Figur steht und wohin sie zöge**. Der
   * Schatten allein sagt nur das Wohin; bei fünfzig Figuren auf dem Brett ist das Woher nicht
   * ohne Weiteres zu finden. Ein Respawn hat keine Spur: die Figur kommt aus dem Vorrat und
   * nicht von einem Feld (§22).
   */
  readonly traces: readonly { readonly from: Hex; readonly to: Hex }[];
}

const NOTHING: PreviewFigures = { ghosts: [], doomed: [], traces: [] };

/**
 * Eine Figur — auf dem Brett oder im Vorrat — als Brettfigur für ein Zielfeld.
 *
 * `poolOwner` wird **nur** für die Suche im Vorrat gebraucht (`capturedOf` liegt je Spieler) und
 * gilt deshalb nur für die handelnde Figur eines Respawns. Eine gestoßene Figur steht auf dem
 * Brett und nennt ihren Besitzer selbst — ihn aus „der andere Spieler" zu erraten wäre bei drei
 * Spielern falsch, und zwar unauffällig: die Farbe des Schattens wäre die eines Unbeteiligten.
 */
function ghostOf(
  state: GameState,
  pieceId: string,
  at: { readonly q: number; readonly r: number },
  poolOwner?: string,
): BoardPiece | undefined {
  const placed = pieceById(state.boardState, pieceId);
  const pooled =
    poolOwner === undefined
      ? undefined
      : capturedOf(state.boardState, poolOwner).find((piece) => piece.id === pieceId);
  const piece = placed ?? pooled;
  if (piece === undefined) {
    return undefined;
  }
  return {
    id: piece.id,
    type: piece.type,
    owner: piece.owner,
    // Die Farbe folgt dem Besitzer, nicht dem Feld — wie bei den echten Figuren.
    sector: (playerOf(state, piece.owner)?.sector ?? 'green') as SectorId,
    at: { q: at.q, r: at.r },
  };
}

export function previewFigures(action: PreviewAction | null, state: GameState): PreviewFigures {
  if (action === null) {
    return NOTHING;
  }

  if (action.type === 'PUSH') {
    const ghosts: BoardPiece[] = [];
    const doomed: string[] = [];
    const traces: { from: Hex; to: Hex }[] = [];

    const victimId = action.victim;
    if (victimId === undefined) {
      return NOTHING;
    }
    if (action.capturedByPush === true || action.victimTo === undefined) {
      // Über den Rand: es gibt kein Feld, auf dem sie stünde.
      doomed.push(victimId);
    } else {
      const victim = ghostOf(state, victimId, action.victimTo);
      if (victim === undefined) return NOTHING;
      ghosts.push(victim);
      if (action.victimAt !== undefined) {
        traces.push({ from: action.victimAt, to: action.victimTo });
      }
    }

    // Der Stoßer nur, wenn er sich bewegt — siehe Modulkopf.
    const pusherTo = action.pusherTo;
    if (pusherTo !== undefined && action.from !== undefined && !hexEquals(pusherTo, action.from)) {
      const pusher = ghostOf(state, action.piece, pusherTo, action.player);
      if (pusher === undefined) return NOTHING;
      ghosts.push(pusher);
      traces.push({ from: action.from, to: pusherTo });
    }

    return { ghosts, doomed, traces };
  }

  const mover = ghostOf(state, action.piece, action.to, action.player);
  if (mover === undefined) {
    return NOTHING;
  }
  // CAPTURE nimmt das Opfer vom Brett; MOVE und RESPAWN nehmen niemanden (§21, §22).
  const doomed = action.victim === undefined ? [] : [action.victim];
  // Ein Respawn hat kein Ausgangsfeld und deshalb keine Spur.
  const traces = action.from === undefined ? [] : [{ from: action.from, to: action.to }];
  return { ghosts: [mover], doomed, traces };
}
